import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload,
  Crop,
  Delete,
  PhotoCamera,
} from '@mui/icons-material';

interface ShireImageUploadProps {
  file: File | null;
  setFile: (file: File | null) => void;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  accept?: string;
  label?: string;
  disabled?: boolean;
}

const ShireImageUpload: React.FC<ShireImageUploadProps> = ({
  file,
  setFile,
  minWidth = 50,
  minHeight = 50,
  maxWidth = 1000,
  maxHeight = 1000,
  accept = 'image/*',
  label = 'Upload Image',
  disabled = false,
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [cropPreview, setCropPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (file) {
      setPreview(URL.createObjectURL(file));
    } else {
      setPreview(null);
    }
  }, [file]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      setFile(droppedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string);
      };
      reader.readAsDataURL(droppedFile);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const renderImageToCanvas = () => {
    if (!preview || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Calculate dimensions maintaining aspect ratio
      let { width, height } = img;
      
      if (width / height > minWidth / minHeight) {
        // Image is wider than target ratio
        height = width / (minWidth / minHeight);
      } else {
        // Image is taller than target ratio
        width = height * (minWidth / minHeight);
      }

      // Set canvas size
      canvas.width = Math.min(width, maxWidth);
      canvas.height = Math.min(height, maxHeight);

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw image centered and cropped
      const scale = Math.min(canvas.width / width, canvas.height / height);
      const scaledWidth = width * scale;
      const scaledHeight = height * scale;
      const x = (canvas.width - scaledWidth) / 2;
      const y = (canvas.height - scaledHeight) / 2;

      ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
    };
    img.src = preview;
  };

  useEffect(() => {
    if (showCropDialog) {
      setTimeout(renderImageToCanvas, 100);
    }
  }, [showCropDialog, preview]);

  const handleCrop = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    
    // Convert to blob and create new file
    canvas.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], file?.name || 'cropped-image.jpg', {
          type: 'image/jpeg',
        });
        setFile(croppedFile);
        setCropPreview(canvas.toDataURL());
        setShowCropDialog(false);
      }
    }, 'image/jpeg', 0.9);
  };

  const handleRemove = () => {
    setFile(null);
    setPreview(null);
    setCropPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {!preview ? (
        <Paper
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            '&:hover': {
              borderColor: disabled ? '#ccc' : '#1976d2',
            },
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={disabled ? undefined : openFileDialog}
        >
          <CloudUpload sx={{ fontSize: 48, color: '#666', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drag and drop an image here, or click to select
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Recommended min size: {minWidth}x{minHeight}<br />
            Recommended max size: {maxWidth}x{maxHeight}
          </Typography>
        </Paper>
      ) : (
        <Box>
          <Paper
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 2,
            }}
          >
            <img
              src={cropPreview || preview}
              alt="Preview"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '300px',
                objectFit: 'cover',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                display: 'flex',
                gap: 1,
              }}
            >
              {/* <IconButton
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.8)' }}
                onClick={() => setShowCropDialog(true)}
                disabled={disabled}
              >
                <Crop />
              </IconButton> */}
              <IconButton
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.8)' }}
                onClick={openFileDialog}
                disabled={disabled}
              >
                <PhotoCamera />
              </IconButton>
              <IconButton
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.8)' }}
                onClick={handleRemove}
                disabled={disabled}
              >
                <Delete />
              </IconButton>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Crop Dialog */}
      <Dialog
        open={showCropDialog}
        onClose={() => setShowCropDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Crop Image</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <canvas
              ref={canvasRef}
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                border: '1px solid #ccc',
              }}
            />
            <Typography variant="body2" color="text.secondary" mt={1}>
              Image will be cropped to maintain {minWidth}x{minHeight} ratio
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCropDialog(false)}>Cancel</Button>
          <Button onClick={handleCrop} variant="contained">
            Apply Crop
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShireImageUpload;
